import { Button } from '@components/button';
import { buttonVariants } from '@components/button/variants';
import { DetailsCard, DetailsCardItem } from '@components/detailsCard';
import { Loader } from '@components/loader';
import { PermissionsAccordion } from '@components/permissionsAccordion';
import { useCheckUserPermissions } from '@hooks/checkUserPermisions';
import { BaseLayout } from '@layouts/base';
import { Page404 } from '@pages/404';
import { useGetPermissionGroups } from '@services/acquirer/users/permissionGroups';
import { useGetAcquirerUser } from '@services/staff/acquirer/users/get';
import { useAcquirerUserResetPassword } from '@services/staff/acquirer/users/resetPassword';
import { Link, createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { FileText, Landmark, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const AcquirerUserPage = () => {
  const { t } = useTranslation('app');
  const { verifyUserPermission } = useCheckUserPermissions();
  const { acquirerId, id, localId } = useParams({
    from: '/u/$localId/_staff/staff/acquirers/$acquirerId/users/$id/',
  });
  const navigate = useNavigate();
  const { data, isPending } = useGetAcquirerUser({ acquirerId, id });
  const { data: permissions } = useGetPermissionGroups();

  const { mutate, isPending: requested } = useAcquirerUserResetPassword({
    id,
    acquirerId,
    callback: () => {
      toast.success(t('toast.password.requestSuccessful'));
    },
  });

  if (isPending) return <Loader />;
  if (!data) return <Page404 />;

  return (
    <BaseLayout
      isChild
      title={data.name}
      actions={[
        verifyUserPermission('acquirer_users_create') && (
          <Button
            key="duplicate"
            variant="outline"
            onClick={() => {
              navigate({
                to: '/u/$localId/staff/acquirers/$acquirerId/users/new',
                params: { acquirerId, localId },
                search: { duplicateFrom: data.id },
              }).catch((err) => console.error(err));
            }}>
            {t('actions.duplicate')}
          </Button>
        ),
        verifyUserPermission(`acquirer_users_password_reset`) && (
          <Button
            key="reset"
            variant="outline"
            loading={requested}
            disabled={requested}
            onClick={() => mutate()}>
            {t('passwordReset')}
          </Button>
        ),
        verifyUserPermission('acquirer_users_update') && (
          <Link
            key="edit"
            to="/u/$localId/staff/acquirers/$acquirerId/users/$id/edit"
            params={{ acquirerId, id: data.id, localId }}
            className={buttonVariants({ variant: 'primary' })}>
            {t('actions.edit')}
          </Link>
        ),
      ]}>
      <DetailsCard>
        <DetailsCardItem
          icon={<Mail size={26} />}
          label={t('table.email')}
          data={data.email}
          column={1}
        />
        <DetailsCardItem
          icon={<Landmark size={26} />}
          label={t('table.acquirer')}
          data={data.acquirer.name}
          column={1}
        />
        <DetailsCardItem
          icon={<FileText size={26} />}
          label={t('table.description')}
          data={data.description}
          column={1}
        />
      </DetailsCard>
      {permissions && (
        <PermissionsAccordion permissions={permissions} selectedPermissions={data.permissions} />
      )}
    </BaseLayout>
  );
};

export const Route = createFileRoute('/u/$localId/_staff/staff/acquirers/$acquirerId/users/$id/')({
  beforeLoad: ({ context }) => {
    if (!context.userHasPermissions(['acquirer_users_read'])) {
      throw redirect({
        to: `/u/$localId/${context.userType}/forbidden`,
      });
    }
  },
  component: AcquirerUserPage,
});
